import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { F } from 'ts-toolbelt';

import { invariant } from '@e-socna/utils';
import { getDocumentName } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { MissionPage_OneMission_QueryDocument } from '../../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { MissionQuotationPage_OneQuotation_QueryDocument } from '../../../pages/MissionQuotation/hooks/useFindOneQuotationQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import type { FolderBusinessEntityStateFormValues } from '../../FolderBusinessEntityState/types/values.type';
import type { UseUpdateFolderBusinessEntityStateSubmit } from '../../FolderBusinessEntityState/types/use-submit.type';

import { documentChoiceForm2ApiMapper } from '../../DocumentTemplates/mappers/document-choice.form2api.mapper';
import { buildQuotationStateUpdateValues } from '../utils/quotation-state-update-values.util';

import { useUpdateMissionQuotationInfoStateMutation } from './useUpdateMissionQuotationInfoStateMutation.hook';
import type { MissionQuotationStateForm_UpdateQuotationInfoState_Result } from './useUpdateMissionQuotationInfoStateMutation.hook';

export const useUpdateMissionQuotationInfoStateSubmitFactory = ({
  isOptionGroupsSelectionAction,
  missionId,
  quotationId,
  onSuccess: handleSuccessFactoryLevel,
}: Readonly<{
  isOptionGroupsSelectionAction: (actionId: Id) => boolean;
  missionId: Id;
  quotationId: Id;
  onSuccess?: (params: Readonly<{ actionId: Id; newStateId: Id }>) => void;
}>): UseUpdateFolderBusinessEntityStateSubmit =>
  function useUpdateMissionQuotationInfoStateSubmit({
    memos,
    onSuccess: handleSuccessSubmitLevel,
  }: F.Parameters<UseUpdateFolderBusinessEntityStateSubmit>[0]) {
    const [formError, setFormError] = useState<readonly string[] | null>(null);

    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const { mutate: updateMissionQuotationInfoStateMutation } =
      useUpdateMissionQuotationInfoStateMutation();

    const handleSubmit = useCallback(
      (
        values: Readonly<FolderBusinessEntityStateFormValues>,
        {
          setSubmitting,
        }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
      ) => {
        setFormError(null);

        const onSuccess = (
          result: Readonly<MissionQuotationStateForm_UpdateQuotationInfoState_Result>,
        ) => {
          enqueueSnackbar('Le statut de votre devis a bien été mis à jour.');

          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(MissionPage_OneMission_QueryDocument),
              {
                id: missionId,
              },
            ],
          });

          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(MissionQuotationPage_OneQuotation_QueryDocument),
              {
                id: quotationId,
              },
            ],
          });

          invariant(
            !!result.updateQuotationInfoState.quotationInfo,
            'Quotation info not found',
          );

          handleSuccessFactoryLevel?.({
            actionId: values.actionId,
            newStateId: result.updateQuotationInfoState.quotationInfo.stateId,
          });

          handleSuccessSubmitLevel?.();

          setSubmitting(false);
        };

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));

          setSubmitting(false);
        };

        updateMissionQuotationInfoStateMutation(
          {
            missionId,
            ...documentChoiceForm2ApiMapper(
              buildQuotationStateUpdateValues(
                values,
                isOptionGroupsSelectionAction,
              ),
            ),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onSuccess,
            onError,
          },
        );
      },
      [
        enqueueSnackbar,
        handleSuccessSubmitLevel,
        memos,
        queryClient,
        updateMissionQuotationInfoStateMutation,
      ],
    );

    return {
      formError,
      handleSubmit,
    };
  };
